import { NotifyOptionsSuccess, NotifyOptionsError } from "@modernary/mui/components/notifications";
import { apiService, type Paging, type PagingApiAndNotifierConfig } from "@modernary/mui/utils/api";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import { apiHosts } from "@/api/constants/apiHosts";
import type { Permit, PermitNew, PermitVersion } from "@/views/permit/permit";
import type { AxiosResponse } from "axios";
import permitService from "./permitService";
import { getFacilityById } from "../facility/facilityApi";
import { LE } from "@/constants/enums";

type VersionInputSource = Partial<PermitVersion>; // | PermitNew | Partial<Permit>

const URL = "permits";
const ENTITY_NAME = "Permit";

const axiosParams = {
  // Set different base URL based on the environment
  baseURL: import.meta.env.VITE_UST_API_BASE_URL,
};

const api = apiService<Permit, Paging<Permit>>(apiHosts.UST, axiosParams);

// /** Builds the version payload shared by permit-create and version CUD. */
export const toVersionInput = (permit: VersionInputSource) => ({
  number: permit.number,
  issueDateTime: permit.issueDateTime,
  effectiveDateTime: permit.effectiveDateTime,
  expirationDateTime: permit.expirationDateTime,
  versionTypeMurn:
    "versionTypeMurn" in permit ? (permit as PermitNew).versionTypeMurn : (permit as Partial<PermitVersion>).typeMurn,
});

/**
 * @param {Object} params Query parameters, varies (TODO improve this)
 * @param {abortCallback} config.abortMethod Callback for when a request has been aborted
 */
const getPermits = (params: any, config: PagingApiAndNotifierConfig = {}) => {
  //TODO:TS
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : URL;

  return api.getPaged(url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successNotifCb,
    errorNotifCb,
  });
};

const getPermitById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.get(URL + "/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

const getNewPermit = async (facilityId?: string | number) => {
  if (facilityId) {
    return Promise.all([getFacilityById(facilityId)]).then(([result]) => {
      const data = permitService.getInitialData();
      data.facility = result.data;
      data.facilityId = result.data.id;

      return { data } as AxiosResponse<PermitNew, any>;
    });
  } else {
    const data = permitService.getInitialData() as PermitNew;
    return { data } as AxiosResponse<PermitNew, any>;
  }
};

const createPermit = (permit: Permit, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.created = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.created = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  // Create the permit together with its initial version (category/type on the permit, the rest on the version).
  const payload = {
    facilityId: permit.facilityId,
    categoryMurn: permit.categoryMurn,
    typeMurn: permit.typeMurn,
    ...toVersionInput(permit),

    versionTypeMurn: (permit as PermitNew).versionTypeMurn ?? LE.PermitVersionType.New,
  };

  return api.post(URL, payload, {
    successNotifCb,
    errorNotifCb,
  });
};

const updatePermit = (permit: Permit, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.updated = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  // Create the permit together with its initial version (category/type on the permit, the rest on the version).
  const payload = {
    facilityId: permit.facilityId,
    categoryMurn: permit.categoryMurn,
    typeMurn: permit.typeMurn,
  };

  return api.put(URL + "/" + permit.id, payload, {
    successNotifCb,
    errorNotifCb,
  });
};

/**
 * @param {Permit} permit This cannot be a 'ref' to a permit
 */
const savePermit = async (permit: Permit, config: PagingApiAndNotifierConfig = {}) => {
  if (!permit.id) {
    return createPermit(permit, config);
  }

  // Editing an existing permit updates category/type only; versions are managed separately.
  return updatePermit(permit, config);
};

const deletePermitById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.deleted = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.deleted = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.delete(URL + "/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

export { createPermit, deletePermitById, getNewPermit, getPermitById, getPermits, savePermit, updatePermit };
